import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const APP_STORE_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Apple+App+Store+download+button-1920w.png';
const GOOGLE_PLAY_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Google+Store+download+button-1920w.png';
const BADGE_SIZES = '180px';

const portalCtaDef: SlotGroupDefinition = {
  label: 'Portal Login CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Access Resident Portal Login',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const appStoreDef: SlotGroupDefinition = {
  label: 'App Store Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://apps.apple.com/us/app/loft-living/id333244247',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const googlePlayDef: SlotGroupDefinition = {
  label: 'Google Play Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://play.google.com/store/apps/details?id=com.activebuilding.residents',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function ResidentAccess() {
  const portalCta = useSlotGroup('residentaccess.portal_cta', portalCtaDef);
  const appStore = useSlotGroup('residentaccess.app_store', appStoreDef);
  const googlePlay = useSlotGroup('residentaccess.google_play', googlePlayDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const portalHref = portalCta.url || '';
  const appStoreHref = appStore.url || '';
  const googlePlayHref = googlePlay.url || '';

  return (
    <section className="ra-section">
      <div ref={sectionRef} className="reveal-fade-up ra-container">
        <EditableSlotGroup groupPath="residentaccess.portal_cta" definition={portalCtaDef}>
          {portalHref ? (
            <a
              href={portalHref}
              target={portalCta._target || '_blank'}
              rel="noopener noreferrer"
              className="ra-portal-btn group/cta relative inline-flex items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case overflow-hidden transition-all duration-300 ra-anim-1"
            >
              <span className="relative z-10">
                {portalCta.label || 'Access Resident Portal Login'}
              </span>
            </a>
          ) : (
            <span className="ra-portal-btn ra-portal-btn--disabled relative inline-flex items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case overflow-hidden ra-anim-1">
              <span className="relative z-10">
                {portalCta.label || 'Access Resident Portal Login'}
              </span>
            </span>
          )}
        </EditableSlotGroup>

        <RichTextContent
          path="residentaccess.app_heading"
          label="App Download Heading"
          as="h2"
          className="ra-app-heading ra-anim-2"
          defaultValue="<p>Download the Resident Mobile App</p>"
        />

        <div className="ra-badges ra-anim-3">
          <EditableSlotGroup groupPath="residentaccess.app_store" definition={appStoreDef}>
            {appStoreHref ? (
              <a
                href={appStoreHref}
                target={appStore._target || '_blank'}
                rel="noopener noreferrer"
                className="ra-badge-link"
              >
                <VibesitesImage
                  url={APP_STORE_BADGE_URL}
                  alt="Download on the App Store"
                  sizes={BADGE_SIZES}
                  className="ra-badge-img"
                />
              </a>
            ) : (
              <span className="ra-badge-link ra-badge-link--muted">
                <VibesitesImage
                  url={APP_STORE_BADGE_URL}
                  alt="Download on the App Store"
                  sizes={BADGE_SIZES}
                  className="ra-badge-img"
                />
              </span>
            )}
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="residentaccess.google_play" definition={googlePlayDef}>
            {googlePlayHref ? (
              <a
                href={googlePlayHref}
                target={googlePlay._target || '_blank'}
                rel="noopener noreferrer"
                className="ra-badge-link"
              >
                <VibesitesImage
                  url={GOOGLE_PLAY_BADGE_URL}
                  alt="Get it on Google Play"
                  sizes={BADGE_SIZES}
                  className="ra-badge-img"
                />
              </a>
            ) : (
              <span className="ra-badge-link ra-badge-link--muted">
                <VibesitesImage
                  url={GOOGLE_PLAY_BADGE_URL}
                  alt="Get it on Google Play"
                  sizes={BADGE_SIZES}
                  className="ra-badge-img"
                />
              </span>
            )}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 3.5rem 0 4.5rem;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .ra-section { padding: 4.5rem 0 5.5rem; }
        }

        .ra-container {
          max-width: 42rem;
          margin: 0 auto;
          padding: 0 1.25rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 1.75rem;
        }
        @media (min-width: 768px) {
          .ra-container { gap: 2rem; }
        }

        /* Match Header .nav-cta-btn (schedule_tour_link): solid primary fill,
           5px radius, surface hover + lift. Styles live here because Header's
           scoped style block does not apply on this page. */
        .ra-portal-btn {
          background: var(--vibe-color-primary);
          border: 0;
          border-radius: 5px;
          color: #ffffff;
          box-shadow: none;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .ra-portal-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .ra-portal-btn:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .ra-portal-btn:hover::before {
          opacity: 0;
        }
        .ra-portal-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .ra-portal-btn--disabled,
        .ra-portal-btn--disabled:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 45%, #9a9a9a);
          color: #ffffff;
          cursor: default;
          transform: none;
          box-shadow: none;
          opacity: 0.85;
        }

        .ra-app-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 3.5vw, 2.15rem);
          font-weight: 400;
          line-height: 1.25;
          color: var(--vibe-color-text, #002147);
          margin: 0;
          max-width: 28rem;
        }
        .ra-app-heading [data-vibesite-richtext] p {
          margin: 0;
        }

        .ra-badges {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.85rem 1rem;
        }

        .ra-badge-link {
          display: inline-flex;
          line-height: 0;
          text-decoration: none;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .ra-badge-link:hover {
          transform: translateY(-2px);
          opacity: 0.92;
        }
        .ra-badge-link--muted {
          cursor: default;
        }

        .ra-badge-img {
          height: 2.75rem;
          width: auto;
          display: block;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .ra-badge-img { height: 3.25rem; }
        }

        @media (prefers-reduced-motion: no-preference) {
          .ra-anim-1,
          .ra-anim-2,
          .ra-anim-3 {
            opacity: 0;
            transform: translateY(12px);
            animation: ra-rise 0.65s ease forwards;
          }
          .ra-anim-1 { animation-delay: 0.05s; }
          .ra-anim-2 { animation-delay: 0.18s; }
          .ra-anim-3 { animation-delay: 0.32s; }
          @keyframes ra-rise {
            to { opacity: 1; transform: translateY(0); }
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.app_heading': {
      label: 'App Download Heading',
      type: 'richtext',
      defaultValue: '<p>Download the Resident Mobile App</p>',
    },
  },
  slotGroups: {
    'residentaccess.portal_cta': portalCtaDef,
    'residentaccess.app_store': appStoreDef,
    'residentaccess.google_play': googlePlayDef,
  },
};

(ResidentAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ResidentAccess;
